import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Icon from '../../../ui/Icon'

import { useApp } from '../../../../contexts/AppContext'
import { BannerKind } from '../../../../types/contexts/AppContext'
import { TimeInterval } from '../../../../types/booking/booking'
import { DayOfWeek, DayOfWeekOrder, formatAvailableDay } from '../../../../utils/dateUtils'

// Building blocks of the schedule cards (price rules, passes). Parts that take the version a value replaced highlight
// whatever that version changed.

export const changedClass = (changed: boolean) => (changed ? ' has-changed' : '')

/** The whole week, with the selected days filled in; days added or dropped since the previous version are highlighted. */
export const ScheduleDays = ({ weekdays, previous }: { weekdays: DayOfWeek[]; previous?: DayOfWeek[] }) => {
  const { t } = useTranslation('common')

  return (
    <div className="schedule-card-chips">
      {DayOfWeekOrder.map(day => {
        const selected = weekdays.includes(day)
        const changed = previous !== undefined && selected !== previous.includes(day)
        return (
          <span key={day} className={`schedule-card-day${selected ? ' is-selected' : ''}${changedClass(changed)}`}>
            {formatAvailableDay(day, t)}
          </span>
        )
      })}
    </div>
  )
}

/** Court chips; courts added since the previous version are highlighted. */
export const ScheduleCourts = ({
  courtIds,
  courtNames,
  previous,
}: {
  courtIds: number[]
  courtNames: Map<number | undefined, string>
  previous?: number[]
}) => (
  <div className="schedule-card-chips">
    {courtIds.map(id => (
      <span key={id} className={`schedule-card-court${changedClass(previous !== undefined && !previous.includes(id))}`}>
        {courtNames.get(id) ?? `#${id}`}
      </span>
    ))}
  </div>
)

/** Up or down arrow for a number that changed since the previous version; nothing when it did not. */
export const Trend = ({ value, before }: { value: number; before?: number }) =>
  before === undefined || before === value ? null : (
    <span className="schedule-card-trend">{value > before ? '▲' : '▼'}</span>
  )

/** "Show history" link of a card header. */
export const HistoryToggle = ({ open, label, onToggle }: { open: boolean; label: string; onToggle: () => void }) => (
  <button type="button" className="schedule-card-meta schedule-card-history-link" aria-expanded={open} onClick={onToggle}>
    <Icon name="history" size={14} />
    <span>{label}</span>
    <Icon name={open ? 'chevUp' : 'chevR'} size={12} />
  </button>
)

/**
 * Open state and versions of one card's history. The versions are fetched the first time the history is expanded and
 * reused afterwards; a failed fetch closes the panel and is retried on the next expand.
 */
export const useVersionHistory = <T,>(load: () => Promise<T[] | null | undefined>) => {
  const { showBanner } = useApp()
  const [open, setOpen] = useState(false)
  const [history, setHistory] = useState<T[] | null>(null)
  const requested = useRef(false)

  const toggle = () => {
    setOpen(value => !value)
    if (open || requested.current) {
      return
    }
    requested.current = true
    load()
      .then(result => setHistory(result ?? []))
      .catch(caught => {
        requested.current = false
        setOpen(false)
        showBanner({ kind: BannerKind.ERR, msg: (caught as Error).message })
      })
  }

  return { open, history, toggle }
}

/** Minutes since midnight of an HH:mm[:ss] time; 00:00 as an end means the end of the day. */
const toMinutes = (time: string, isEnd = false) => {
  const [hours, minutes] = time.split(':').map(Number)
  const value = hours * 60 + minutes
  return isEnd && value === 0 ? 24 * 60 : value
}

const toClock = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`

/**
 * Opening hours as a thin track, with the given time windows drawn on it.
 */
export const ScheduleTimeline = ({
  intervals,
  openTime,
  closeTime,
}: {
  intervals: TimeInterval[]
  openTime?: string
  closeTime?: string
}) => {
  const open = openTime ? toMinutes(openTime) : 6 * 60
  const close = closeTime ? toMinutes(closeTime, true) : 24 * 60
  if (close <= open) {
    return null
  }

  const span = close - open
  const clamp = (minutes: number) => Math.min(Math.max(minutes, open), close)
  const at = (minutes: number) => `${((minutes - open) / span) * 100}%`
  const ranges = intervals.map(interval => ({
    start: clamp(toMinutes(interval.startTime)),
    end: clamp(toMinutes(interval.endTime, true)),
  }))

  // Hour marks every 2h on long days, every hour on short ones, always including the opening and closing time.
  const step = span > 8 * 60 ? 120 : 60
  const marks: number[] = []
  for (let minute = open; minute < close; minute += step) {
    marks.push(minute)
  }
  // A mark too close to closing time would collide with its label.
  if (marks.length > 1 && close - marks[marks.length - 1] < step / 2) {
    marks.pop()
  }
  marks.push(close)
  const dots = Array.from(new Set([...marks, ...ranges.flatMap(range => [range.start, range.end])])).sort((a, b) => a - b)
  const inRange = (minute: number) => ranges.some(range => minute >= range.start && minute <= range.end)

  return (
    <div className="schedule-card-timeline d-none d-md-block" aria-hidden="true">
      <div className="schedule-card-timeline-track">
        {ranges.map(range => (
          <span
            key={`${range.start}-${range.end}`}
            className="schedule-card-timeline-range"
            style={{ left: at(range.start), width: `${((range.end - range.start) / span) * 100}%` }}
          />
        ))}
        {dots.map(minute => (
          <span
            key={minute}
            className={`schedule-card-timeline-dot${inRange(minute) ? ' is-in' : ''}`}
            style={{ left: at(minute) }}
          />
        ))}
      </div>
      <div className="schedule-card-timeline-labels">
        {marks.map(minute => (
          <span key={minute} className="mono" style={{ left: at(minute) }}>
            {toClock(minute)}
          </span>
        ))}
      </div>
    </div>
  )
}
